import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PhoneCallDetail, PhoneCallTranscriptView, PhoneCallView } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { API_ROOT } from 'env';
import { Toast } from 'widgets';

export const CallQueryKeys = {
    CallsQueryKey: (householdGUID: string) => ['ZoomPhoneCalls', householdGUID] as const,
    CallDetailQueryKey: (zoomPhoneCallGUID: string) => ['ZoomPhoneCall', zoomPhoneCallGUID] as const,
    CallTranscriptQueryKey: (zoomPhoneCallGUID: string) => ['ZoomPhoneCallTranscript', zoomPhoneCallGUID] as const,
} as const;

export const useCallsQuery = (householdGUID: string, enabled = true) =>
    useQuery<PhoneCallView[]>({
        enabled: !!householdGUID && enabled,
        queryKey: CallQueryKeys.CallsQueryKey(householdGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().listCallsByHousehold(householdGUID);
            return response || [];
        },
    });

export const useCallDetailQuery = (zoomPhoneCallGUID: string) =>
    useQuery<PhoneCallDetail | null>({
        enabled: !!zoomPhoneCallGUID,
        queryKey: CallQueryKeys.CallDetailQueryKey(zoomPhoneCallGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().getCall(zoomPhoneCallGUID);
            return response;
        },
    });

export const useCallTranscriptQuery = (zoomPhoneCallGUID: string, hasTranscript: boolean) =>
    useQuery<PhoneCallTranscriptView | null>({
        enabled: !!zoomPhoneCallGUID && hasTranscript,
        queryKey: CallQueryKeys.CallTranscriptQueryKey(zoomPhoneCallGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().getCallTranscript(zoomPhoneCallGUID);
            return response;
        },
    });

/** Recording audio is proxied from Zoom by the backend and streamed with the session's cookies. */
export const callRecordingUrl = (zoomPhoneCallGUID: string, download = false): string =>
    `${API_ROOT}/customer/zoom-phone/calls/${zoomPhoneCallGUID}/recording${download ? '?download=true' : ''}`;

export const useAttachNoteToCallMutation = (zoomPhoneCallGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (noteGUID: string) => {
            await CustomerApi().attachNoteToCall(zoomPhoneCallGUID, { noteGUID });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CallQueryKeys.CallDetailQueryKey(zoomPhoneCallGUID) });
            queryClient.invalidateQueries({ queryKey: ['ZoomPhoneCalls'] });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to attach note to call.');
        },
    });
};
